import { DetailedListOfSitesResponse } from '@core/api/sites/sites.models';

import { SiteStoreModel } from '@domain/dashboard/dashboard.models';
import {
  EvseStatusStoreModelEnum,
  HealthGradeStoreModelEnum,
  SiteTypeStoreModelEnum,
  VehicleOperationalStatusStoreModelEnum,
} from '@domain/single-site/single-site.enums';
import { mapSiteStatsApiModelToStoreModel } from '@domain/sites/sites-models.mappings';
import {
  ChargerOperationalStatusStoreModelEnum,
  SiteAlertStateStoreModelEnum,
  SiteReadinessStateStoreModelEnum,
  SitesSortByStoreModelEnum,
} from '@domain/sites/sites.enums';
import { SiteStatsStoreModel, SitesFilterStoreModel } from '@domain/sites/sites.models';

import { AtAGlanceSiteCardViewModel } from '@shared/components/at-a-glance-site-card';
import { EVSE_STATUS_PROPERTIES_MAP } from '@shared/components/evse-status-icon';
import { VEHICLE_STATUS_PROPERTIES_MAP } from '@shared/components/vehicle-operational-status-icon';
import { SortDirectionStoreModelEnum } from '@shared/enums/sort-direction.enum';
import { compareByString, sortSitesByName } from '@shared/utils/sort-utils';

import { ChargersStatusViewModel } from '../components/depot-locations-map/components/chargers-status';
import { SiteDetailsViewModel } from '../components/depot-locations-map/components/site-details';
import { SiteMarkerDataViewModel, SiteMarkerViewModel } from '../components/depot-locations-map/components/site-marker';
import { SiteMarkerContentViewModel } from '../components/depot-locations-map/components/site-marker-content';
import { SitesStatusViewModel } from '../components/depot-locations-map/components/sites-status';
import { DepotLocationsMapViewModel } from '../components/depot-locations-map/depot-locations-map.view-model';
import { SiteCardViewModel } from '../components/site-card';
import { SitesFilterViewModel } from '../components/sites-filter/sites-filter.view-model';
import { SitesTableDataSourceViewModel, SitesTableViewModel } from '../components/sites-table/sites-table.view-model';
import { SitesTableSortPropertyStoreModelEnum } from '../enums/sites-table-sort-property.enum';

export const mapDetailedListOfSitesResponseApiModelToSiteStatsStoreModels = (
  response: DetailedListOfSitesResponse
): SiteStatsStoreModel[] => {
  return sortSitesByName(response.sites.map((site) => mapSiteStatsApiModelToStoreModel(site)));
};

export const mapSitesFilterAndSiteDetailsStoreModelToSitesFilterViewModel = (
  filters: SitesFilterStoreModel,
  sites: SiteStatsStoreModel[],
  hasViewAnySitesPermission: boolean
): SitesFilterViewModel => {
  const possibleTenants = sites
    .reduce((acc, site) => {
      if (site.tenant && !acc.includes(site.tenant)) {
        acc.push(site.tenant);
      }
      return acc;
    }, [] as string[])
    .sort((a, b) => a.localeCompare(b));

  const possibleRegions = sites.reduce((acc, site) => {
    if (site.region && !acc.includes(site.region)) {
      acc.push(site.region);
    }
    return acc;
  }, [] as string[]);

  return {
    possibleTenants,
    possibleRegions,
    search: filters.search,
    selectedTenants: filters.tenants,
    selectedRegions: filters.regions,
    selectedAlertStates: filters.alertStates,
    selectedChargerStates: filters.chargerStates,
    selectedReadinessStates: filters.readinessStates,
    hasViewAnySitesPermission,
    defaultFilterFormValue: {
      search: '',
      tenants: possibleTenants,
      regions: possibleRegions,
      alertStates: Object.values(SiteAlertStateStoreModelEnum),
      chargerStates: Object.values(ChargerOperationalStatusStoreModelEnum),
      readinessStates: Object.values(SiteReadinessStateStoreModelEnum),
    },
  };
};

export const mapSiteSummariesToSitesFilterViewModel = (
  filters: SitesFilterStoreModel | undefined,
  sites: SiteStoreModel[],
  hasViewAnySitesPermission: boolean
): SitesFilterViewModel => {
  const possibleTenants = sites
    .reduce((acc, site) => {
      if (site.tenant && !acc.includes(site.tenant)) {
        acc.push(site.tenant);
      }
      return acc;
    }, [] as string[])
    .sort((a, b) => a.localeCompare(b));

  return {
    possibleTenants,
    possibleRegions: [],
    search: filters?.search ?? '',
    selectedTenants: filters?.tenants ?? possibleTenants,
    selectedRegions: [],
    selectedAlertStates: Object.values(SiteAlertStateStoreModelEnum),
    selectedChargerStates: Object.values(ChargerOperationalStatusStoreModelEnum),
    selectedReadinessStates: Object.values(SiteReadinessStateStoreModelEnum),
    hasViewAnySitesPermission,
    defaultFilterFormValue: {
      search: '',
      tenants: possibleTenants,
      regions: [],
      alertStates: Object.values(SiteAlertStateStoreModelEnum),
      chargerStates: Object.values(ChargerOperationalStatusStoreModelEnum),
      readinessStates: Object.values(SiteReadinessStateStoreModelEnum),
    },
  };
};

export const getFilteredSites = (filters: SitesFilterStoreModel, sites: SiteStatsStoreModel[]): SiteStatsStoreModel[] => {
  return sites.filter((site) => {
    const search = filters.search.trim().toLowerCase();

    const allChargerStatesAreAllowed = Object.values(ChargerOperationalStatusStoreModelEnum).every((chargerState) =>
      filters.chargerStates.includes(chargerState)
    );

    return (
      ((search && site.name.trim().toLowerCase().includes(search)) || !search) &&
      filters.tenants.includes(site.tenant) &&
      filters.regions.includes(site.region) &&
      filters.alertStates.includes(site.alertState) &&
      (allChargerStatesAreAllowed ? true : filters.chargerStates.some((chargerState) => site.chargersPerState[chargerState] > 0)) &&
      filters.readinessStates.includes(site.readinessState)
    );
  });
};

export const getSortedSitesForCards = (sites: SiteStatsStoreModel[], sortBy: SitesSortByStoreModelEnum): SiteStatsStoreModel[] => {
  sites = [...sites];
  const sortDirection =
    sortBy === SitesSortByStoreModelEnum.AlphabeticallyDescending ? SortDirectionStoreModelEnum.Desc : SortDirectionStoreModelEnum.Asc;
  return sites.sort((a, b) => compareByString(a.name, b.name, sortDirection));
};

export const getFilteredSiteSummaries = (filters: SitesFilterStoreModel | undefined, sites: SiteStoreModel[]): SiteStoreModel[] => {
  if (!filters) {
    return sites;
  }

  const search = filters.search.trim().toLowerCase();
  return sites.filter((site) => {
    return (
      ((search && site.name.trim().toLowerCase().includes(search)) || !search) &&
      (filters.tenants.length === 0 || filters.tenants.includes(site.tenant ?? ''))
    );
  });
};

export const getMobileFilteredSiteSummaries = (sites: SiteStoreModel[], selectedTenants: string[], search: string): SiteStoreModel[] => {
  const normalisedSearch = search.trim().toLowerCase();

  return sites.filter(
    (site) =>
      selectedTenants.includes(site.tenant ?? '') &&
      ((normalisedSearch && site.name.trim().toLowerCase().includes(normalisedSearch)) || !normalisedSearch)
  );
};

export const getSortedSiteSummariesForCards = (sites: SiteStoreModel[], sortBy: SitesSortByStoreModelEnum): SiteStoreModel[] => {
  const sortDirection =
    sortBy === SitesSortByStoreModelEnum.AlphabeticallyDescending ? SortDirectionStoreModelEnum.Desc : SortDirectionStoreModelEnum.Asc;
  return [...sites].sort((a, b) => compareByString(a.name, b.name, sortDirection));
};

export const mapSitesToDepotLocationsMapViewModel = (
  allSites: SiteStatsStoreModel[],
  filteredSites: SiteStatsStoreModel[],
  selectedSiteId?: string
): DepotLocationsMapViewModel => {
  return {
    markers: mapSitesToSiteMarkerViewModels(filteredSites, selectedSiteId),
    sitesStatus: mapSitesToSitesStatus(allSites),
    chargersStatus: mapSitesToChargersStatusViewModel(allSites),
    selectedMarkerId: selectedSiteId ?? null,
  };
};

export const mapSiteSummariesToDepotLocationsMapViewModel = (
  sites: SiteStoreModel[],
  selectedSiteId?: string
): DepotLocationsMapViewModel => {
  return {
    markers: sites.flatMap((site): SiteMarkerViewModel[] => {
      if (!site.centerCoordinates) {
        return [];
      }

      return [
        {
          id: site.id,
          coords: [site.centerCoordinates.longitude, site.centerCoordinates.latitude],
          data: {
            contentData: {
              name: site.name,
              siteType: site.siteType,
              isLoadingDetails: true,
              faded: !!selectedSiteId && selectedSiteId !== site.id,
            },
            siteDetails: {
              siteId: site.id,
              isLoadingDetails: true,
              siteType: SiteTypeStoreModelEnum.Fleet,
              iconsVm: {
                readyCount: 0,
                chargingCount: 0,
                queuedCount: 0,
                faultedCount: 0,
                elseCount: 0,
              },
              pocPowerKw: 0,
              pocLimitKw: 0,
              pocHealthGrade: HealthGradeStoreModelEnum.Good,
              evLoadKw: 0,
            },
          },
        },
      ];
    }),
    sitesStatus: mapSitesToSitesStatus([]),
    chargersStatus: mapSitesToChargersStatusViewModel([]),
    selectedMarkerId: selectedSiteId ?? null,
    isLoadingDetails: true,
  };
};

export const mapSitesToSiteMarkerViewModels = (sites: SiteStatsStoreModel[], selectedSiteId?: string): SiteMarkerViewModel[] => {
  return sites.map((site): SiteMarkerViewModel => {
    return {
      id: site.id,
      coords: [site.centerCoordinates.longitude, site.centerCoordinates.latitude],
      data: mapSiteToSiteMarkerDataViewModel(site, selectedSiteId),
    };
  });
};

export const mapSiteToSiteMarkerDataViewModel = (site: SiteStatsStoreModel, selectedSiteId?: string): SiteMarkerDataViewModel => {
  return {
    contentData: mapSiteToSiteMarkerContentViewModel(site, selectedSiteId),
    siteDetails: mapSiteToSiteDetailsViewModel(site),
  };
};

export const mapSiteToSiteMarkerContentViewModel = (site: SiteStatsStoreModel, selectedSiteId?: string): SiteMarkerContentViewModel => {
  return site.siteType === SiteTypeStoreModelEnum.Fleet
    ? {
        name: site.name,
        siteType: site.siteType,
        vehiclesCount: Object.values(site.vehiclesPerState!).reduce((sum, count) => sum + count, 0),
        status: site.alertState,
        faded: !!selectedSiteId && selectedSiteId !== site.id,
      }
    : {
        name: site.name,
        siteType: site.siteType,
        evsesCount: Object.values(site.evsesPerState!).reduce((sum, count) => sum + count, 0),
        status: site.alertState,
        faded: !!selectedSiteId && selectedSiteId !== site.id,
      };
};

export const mapSiteToSiteDetailsViewModel = (site: SiteStatsStoreModel): SiteDetailsViewModel => {
  return site.siteType === SiteTypeStoreModelEnum.Fleet
    ? {
        siteId: site.id,
        siteType: site.siteType,
        iconsVm: {
          readyCount: site.vehiclesPerState![VehicleOperationalStatusStoreModelEnum.Ready],
          chargingCount: site.vehiclesPerState![VehicleOperationalStatusStoreModelEnum.Charging],
          queuedCount: site.vehiclesPerState![VehicleOperationalStatusStoreModelEnum.Queued],
          faultedCount: site.vehiclesPerState![VehicleOperationalStatusStoreModelEnum.Faulted],
          elseCount:
            site.vehiclesPerState![VehicleOperationalStatusStoreModelEnum.InDepot] +
            site.vehiclesPerState![VehicleOperationalStatusStoreModelEnum.OnTheRoad] +
            site.vehiclesPerState![VehicleOperationalStatusStoreModelEnum.Unknown],
        },
        pocPowerKw: site.pocPowerKw,
        pocLimitKw: site.pocLimitKw,
        pocHealthGrade: site.pocHealthGrade,
        bessPowerKw: site.bessPowerKw,
        evLoadKw: site.evLoadKw,
        bessSocPercentage: site.bessSocPercentage,
        bessSocEnergyKwh: site.bessSocEnergyKwh,
      }
    : {
        siteId: site.id,
        siteType: site.siteType,
        iconsVm: {
          dominantConnectorType: site.dominantConnectorType,
          availableCount: site.evsesPerState![EvseStatusStoreModelEnum.Available],
          chargingCount: site.evsesPerState![EvseStatusStoreModelEnum.Charging],
          queuedCount: site.evsesPerState![EvseStatusStoreModelEnum.Queued],
          faultedCount: site.evsesPerState![EvseStatusStoreModelEnum.Faulted],
          elseCount: site.evsesPerState![EvseStatusStoreModelEnum.Maintenance] + site.evsesPerState![EvseStatusStoreModelEnum.Unknown],
        },
        pocPowerKw: site.pocPowerKw,
        pocLimitKw: site.pocLimitKw,
        pocHealthGrade: site.pocHealthGrade,
        bessPowerKw: site.bessPowerKw,
        evLoadKw: site.evLoadKw,
        bessSocPercentage: site.bessSocPercentage,
        bessSocEnergyKwh: site.bessSocEnergyKwh,
      };
};

export const mapSitesToSitesStatus = (sites: SiteStatsStoreModel[]): SitesStatusViewModel => {
  return {
    counts: sites.reduce(
      (acc, site) => {
        if (!acc[site.alertState]) {
          acc[site.alertState] = 0;
        }
        acc[site.alertState] += 1;
        return acc;
      },
      {
        [SiteAlertStateStoreModelEnum.Alerts]: 0,
        [SiteAlertStateStoreModelEnum.Issues]: 0,
        [SiteAlertStateStoreModelEnum.NoIssues]: 0,
      } as Record<SiteAlertStateStoreModelEnum, number>
    ),
  };
};

export const mapSitesToChargersStatusViewModel = (sites: SiteStatsStoreModel[]): ChargersStatusViewModel => {
  return {
    counts: sites.reduce(
      (acc, site) => {
        Object.entries(site.chargersPerState).forEach(([chargerState, count]) => {
          if (!acc[chargerState as ChargerOperationalStatusStoreModelEnum]) {
            acc[chargerState as ChargerOperationalStatusStoreModelEnum] = 0;
          }
          acc[chargerState as ChargerOperationalStatusStoreModelEnum] += count;
        });
        return acc;
      },
      {
        [ChargerOperationalStatusStoreModelEnum.Operational]: 0,
        [ChargerOperationalStatusStoreModelEnum.Maintenance]: 0,
        [ChargerOperationalStatusStoreModelEnum.Faulted]: 0,
        [ChargerOperationalStatusStoreModelEnum.Offline]: 0,
      } as Record<ChargerOperationalStatusStoreModelEnum, number>
    ),
    maintenanceChargersSitesCount: sites.filter((site) => site.chargersPerState[ChargerOperationalStatusStoreModelEnum.Maintenance] > 0)
      .length,
    faultedChargersSitesCount: sites.filter((site) => site.chargersPerState[ChargerOperationalStatusStoreModelEnum.Faulted] > 0).length,
    offlineChargersSitesCount: sites.filter((site) => site.chargersPerState[ChargerOperationalStatusStoreModelEnum.Offline] > 0).length,
  };
};

export const mapSitesToSiteCardViewModels = (
  sites: SiteStatsStoreModel[],
  sitesCollapsedById: Record<string, boolean>
): SiteCardViewModel[] => {
  return sites.map((site) => mapSiteToSiteCardViewModel(site, sitesCollapsedById));
};

export const mapSiteSummariesToSiteCardViewModels = (sites: SiteStoreModel[], collapsed: boolean): SiteCardViewModel[] => {
  return sites.map((site) => {
    if (site.siteType === SiteTypeStoreModelEnum.Fleet) {
      return {
        siteId: site.id,
        siteRegion: '',
        siteTenant: site.tenant ?? '',
        siteName: site.name,
        siteType: site.siteType,
        collapsed,
        isLoadingDetails: true,
        depotOverviewVehiclesChartVm: { data: {}, propertiesMap: VEHICLE_STATUS_PROPERTIES_MAP },
        chargers: 0,
        totalChargers: 0,
        pocLoadKw: 0,
        pocLimitKw: 0,
        pocHealthGrade: HealthGradeStoreModelEnum.Good,
        evLoadKw: 0,
      };
    }

    return {
      siteId: site.id,
      siteRegion: '',
      siteTenant: site.tenant ?? '',
      siteName: site.name,
      siteType: site.siteType,
      collapsed,
      isLoadingDetails: true,
      depotOverviewEvsesChartVm: { data: {}, propertiesMap: EVSE_STATUS_PROPERTIES_MAP },
      pocLoadKw: 0,
      pocLimitKw: 0,
      pocHealthGrade: HealthGradeStoreModelEnum.Good,
      evLoadKw: 0,
    };
  });
};

export const mapSiteToSiteCardViewModel = (site: SiteStatsStoreModel, sitesCollapsedById: Record<string, boolean>): SiteCardViewModel => {
  if (site.siteType === SiteTypeStoreModelEnum.Fleet) {
    const vehiclesPerState: Partial<Record<VehicleOperationalStatusStoreModelEnum, number>> = {};
    Object.keys(site.vehiclesPerState!).forEach((key) => {
      const typedKey = key as VehicleOperationalStatusStoreModelEnum;
      if (site.vehiclesPerState![typedKey] > 0) {
        vehiclesPerState![typedKey] = site.vehiclesPerState![typedKey];
      }
    });

    return {
      siteId: site.id,
      siteRegion: site.region,
      siteTenant: site.tenant,
      siteName: site.name,
      siteType: site.siteType,
      collapsed: sitesCollapsedById[site.id],
      depotOverviewVehiclesChartVm: {
        data: vehiclesPerState,
        propertiesMap: VEHICLE_STATUS_PROPERTIES_MAP,
      },
      predictedDeparturePercentage: site.predictedDeparture && site.predictedDeparture * 100,
      predictedDepartureHealthGrade: site.predictedDepartureHealthGrade,
      bessStatus: site.bessStatus,
      chargers: site.activeEvses,
      totalChargers: site.totalEvses,
      chargersHealthGrade: site.evsesHealthGrade,
      pocLoadKw: site.pocPowerKw,
      pocLimitKw: site.pocLimitKw,
      pocHealthGrade: site.pocHealthGrade,
      evLoadKw: site.evLoadKw,
      bessPowerKw: site.bessPowerKw,
      bessSocKwh: site.bessSocEnergyKwh,
      bessSocPercentage: site.bessSocPercentage,
      bessSocHealthGrade: site.bessSocHealthGrade,
    };
  } else {
    const evsesPerState: Partial<Record<EvseStatusStoreModelEnum, number>> = {};
    Object.keys(site.evsesPerState!).forEach((key) => {
      const typedKey = key as EvseStatusStoreModelEnum;
      if (site.evsesPerState![typedKey] > 0) {
        evsesPerState![typedKey] = site.evsesPerState![typedKey];
      }
    });

    return {
      siteId: site.id,
      siteRegion: site.region,
      siteTenant: site.tenant,
      siteName: site.name,
      siteType: site.siteType,
      collapsed: sitesCollapsedById[site.id],
      depotOverviewEvsesChartVm: evsesPerState && {
        data: evsesPerState,
        propertiesMap: EVSE_STATUS_PROPERTIES_MAP,
      },
      bessStatus: site.bessStatus,
      pocLoadKw: site.pocPowerKw,
      pocLimitKw: site.pocLimitKw,
      pocHealthGrade: site.pocHealthGrade,
      evLoadKw: site.evLoadKw,
      bessPowerKw: site.bessPowerKw,
      bessSocKwh: site.bessSocEnergyKwh,
      bessSocPercentage: site.bessSocPercentage,
      bessSocHealthGrade: site.bessSocHealthGrade,
    };
  }
};

export const mapSiteAlertStateStoreModelEnumToHealthGradeStoreModelEnum = (
  status: SiteAlertStateStoreModelEnum
): HealthGradeStoreModelEnum => {
  switch (status) {
    case SiteAlertStateStoreModelEnum.Alerts: {
      return HealthGradeStoreModelEnum.Error;
    }
    case SiteAlertStateStoreModelEnum.Issues: {
      return HealthGradeStoreModelEnum.Warning;
    }
    case SiteAlertStateStoreModelEnum.NoIssues: {
      return HealthGradeStoreModelEnum.Good;
    }
  }
};

export const mapSitesToSitesTableViewModel = (
  sites: SiteStatsStoreModel[],
  sitesTableSortProperty: SitesTableSortPropertyStoreModelEnum,
  sitesTableSortDirection: SortDirectionStoreModelEnum,
  hasViewAnySitesPermission: boolean
): SitesTableViewModel => {
  const vms = sites.map((site): SitesTableDataSourceViewModel => mapSiteToSitesTableDataSourceViewModel(site));
  const sortedVms = sortSiteVms(vms, sitesTableSortProperty, sitesTableSortDirection);

  return {
    preferredSiteType: sites[0]?.siteType ?? SiteTypeStoreModelEnum.Fleet,
    dataSource: sortedVms,
    sortProperty: sitesTableSortProperty,
    sortDirection: sitesTableSortDirection,
    hasViewAnySitesPermission,
  };
};

export const mapSiteSummariesToSitesTableViewModel = (
  sites: SiteStoreModel[],
  sitesTableSortProperty: SitesTableSortPropertyStoreModelEnum,
  sitesTableSortDirection: SortDirectionStoreModelEnum,
  hasViewAnySitesPermission: boolean
): SitesTableViewModel => {
  const dataSource = sites.map((site) => ({
    siteId: site.id,
    siteName: site.name,
    siteRegion: '',
    siteTenant: site.tenant ?? '',
    isLoadingDetails: true,
  }));

  return {
    preferredSiteType: sites[0]?.siteType ?? SiteTypeStoreModelEnum.Fleet,
    dataSource:
      sitesTableSortProperty === SitesTableSortPropertyStoreModelEnum.Tenant ||
      sitesTableSortProperty === SitesTableSortPropertyStoreModelEnum.SiteName
        ? sortSiteVms(dataSource, sitesTableSortProperty, sitesTableSortDirection)
        : dataSource,
    sortProperty: sitesTableSortProperty,
    sortDirection: sitesTableSortDirection,
    hasViewAnySitesPermission,
  };
};

export const sortSiteVms = (
  sites: SitesTableDataSourceViewModel[],
  sitesTableSortProperty: SitesTableSortPropertyStoreModelEnum,
  sitesTableSortDirection: SortDirectionStoreModelEnum
): SitesTableDataSourceViewModel[] => {
  const sortedSites = [...sites];

  const healthGradeOrder = [HealthGradeStoreModelEnum.Good, HealthGradeStoreModelEnum.Warning, HealthGradeStoreModelEnum.Error];

  sortedSites.sort((a, b) => {
    let result: number;
    switch (sitesTableSortProperty) {
      case SitesTableSortPropertyStoreModelEnum.Tenant: {
        result = compareByString(a.siteTenant, b.siteTenant, SortDirectionStoreModelEnum.Asc);

        break;
      }
      case SitesTableSortPropertyStoreModelEnum.SiteName: {
        result = compareByString(a.siteName, b.siteName, SortDirectionStoreModelEnum.Asc);

        break;
      }
      case SitesTableSortPropertyStoreModelEnum.Region: {
        result = compareByString(a.siteRegion, b.siteRegion, SortDirectionStoreModelEnum.Asc);

        break;
      }
      case SitesTableSortPropertyStoreModelEnum.Entities: {
        result = (a.totalEntities ?? 0) - (b.totalEntities ?? 0);
        break;
      }
      case SitesTableSortPropertyStoreModelEnum.Performance: {
        result = (a.performancePercentage ?? 0) - (b.performancePercentage ?? 0);
        break;
      }
      case SitesTableSortPropertyStoreModelEnum.Status: {
        result = (a.criticalErrorsCount ?? 0) - (b.criticalErrorsCount ?? 0);
        break;
      }
      case SitesTableSortPropertyStoreModelEnum.EntitiesPerState: {
        result = sortSitesByEntitiesPerState(a, b);
        break;
      }
      case SitesTableSortPropertyStoreModelEnum.Health: {
        result =
          healthGradeOrder.indexOf(a.healthGrade ?? HealthGradeStoreModelEnum.Good) -
          healthGradeOrder.indexOf(b.healthGrade ?? HealthGradeStoreModelEnum.Good);
        break;
      }
    }

    if (sitesTableSortDirection === SortDirectionStoreModelEnum.Desc) {
      return result * -1;
    }

    return result;
  });

  return sortedSites;
};

export const sortSitesByEntitiesPerState = (a: SitesTableDataSourceViewModel, b: SitesTableDataSourceViewModel): number => {
  let result = 0;
  const vehicleStateOrder = [
    VehicleOperationalStatusStoreModelEnum.Ready,
    VehicleOperationalStatusStoreModelEnum.Charging,
    VehicleOperationalStatusStoreModelEnum.Queued,
    VehicleOperationalStatusStoreModelEnum.Faulted,
    VehicleOperationalStatusStoreModelEnum.InDepot,
    VehicleOperationalStatusStoreModelEnum.OnTheRoad,
    VehicleOperationalStatusStoreModelEnum.Unknown,
  ];

  const evseStateOrder = [
    EvseStatusStoreModelEnum.Charging,
    EvseStatusStoreModelEnum.Queued,
    EvseStatusStoreModelEnum.Available,
    EvseStatusStoreModelEnum.Faulted,
    EvseStatusStoreModelEnum.Maintenance,
  ];

  let stateIdx = 0;

  while (result === 0 && stateIdx < vehicleStateOrder.length) {
    const aIdx = a.vehiclesPerState
      ? (a.vehiclesPerState?.[vehicleStateOrder[stateIdx]] ?? 0)
      : (a.evsesPerState?.[evseStateOrder[stateIdx]] ?? 0);

    const bIdx = b.vehiclesPerState
      ? (b.vehiclesPerState?.[vehicleStateOrder[stateIdx]] ?? 0)
      : (b.evsesPerState?.[evseStateOrder[stateIdx]] ?? 0);

    result = aIdx - bIdx;
    stateIdx += 1;
  }

  return result;
};

export const mapSiteToSitesTableDataSourceViewModel = (site: SiteStatsStoreModel): SitesTableDataSourceViewModel => {
  return {
    siteId: site.id,
    siteName: site.name,
    siteRegion: site.region,
    siteTenant: site.tenant,
    totalEntities: site.vehiclesPerState
      ? Object.values(site.vehiclesPerState).reduce((acc, count) => acc + count, 0)
      : Object.values(site.evsesPerState!).reduce((acc, count) => acc + count, 0),
    performancePercentage: site.performancePercentage * 100,
    criticalErrorsCount: site.criticalErrorsCount,
    majorErrorsCount: site.majorErrorsCount,
    vehiclesPerState: site.vehiclesPerState,
    evsesPerState: site.evsesPerState,
    healthGrade: mapSiteAlertStateStoreModelEnumToHealthGradeStoreModelEnum(site.alertState),
  };
};

export const mapSiteToMobileSiteCardViewModel = (site: SiteStatsStoreModel): AtAGlanceSiteCardViewModel => {
  return {
    siteId: site.id,
    siteName: site.name,
    siteTenant: site.tenant,
    vehiclesPerState: site.vehiclesPerState,
    evsesPerState: site.evsesPerState,
  };
};

export const mapSiteSummaryToMobileSiteCardViewModel = (site: SiteStoreModel): AtAGlanceSiteCardViewModel => {
  return {
    siteId: site.id,
    siteName: site.name,
    siteTenant: site.tenant ?? '',
    isLoadingDetails: true,
  };
};
